/**
 * @fileoverview Dispute Management Hook
 * 
 * Manages disputes raised by clients or vendors when there are disagreements
 * about job completion, delivery quality, or payment. Disputes trigger admin
 * involvement for resolution and temporarily halt payment processing.
 * 
 * Dispute Workflow:
 * 1. Party raises dispute with detailed reason and supporting documents
 * 2. Job status changes to 'disputed'
 * 3. Admin is notified via edge function
 * 4. Admin reviews evidence from both parties
 * 5. Admin resolves dispute in favor of client or vendor
 * 6. Job status updated based on resolution
 * 7. Payment processed or refunded accordingly
 * 
 * Dispute Reasons (Common):
 * - Quality issues: Delivered items don't meet specifications
 * - Missing items: Incomplete delivery
 * - Damaged goods: Items damaged during delivery
 * - Late delivery: Delivery past agreed timeline
 * - Incorrect items: Wrong products delivered
 * - Payment disagreement: Dispute over charges or adjustments
 * 
 * Resolution Types:
 * - resolved_for_client: Client is correct, vendor at fault
 * - resolved_for_vendor: Vendor is correct, client complaint invalid
 * 
 * Impact on Job Status:
 * - Creating dispute → job status: 'disputed'
 * - Resolved for client → job status: 'accepted' (client gets refund/credit)
 * - Resolved for vendor → job status: 'rejected' (vendor gets paid)
 * 
 * Key Features:
 * - Create disputes with supporting documentation
 * - Admin-only resolution capability
 * - Automatic job status synchronization
 * - Admin notification via edge function
 * - Resolution notes for record keeping
 * - Link to cancellation requests
 * 
 * @module useDisputes
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dispute, DisputeDetails } from '@/lib/types/completion';

/**
 * Hook for managing disputes
 * 
 * Provides functionality to create and resolve disputes. Disputes can be
 * created by any party when there's a disagreement about job completion,
 * quality, or payment. Only admins can resolve disputes.
 * 
 * Data Loading:
 * - Fetches all disputes on mount
 * - Orders by creation date (most recent first)
 * - Includes all dispute details and status
 * 
 * Notifications:
 * - Admin notified immediately when dispute created
 * - Notification sent via dispute-notify edge function
 * - Both parties notified of resolution
 * 
 * @returns Object with disputes data and management functions
 * 
 * @example
 * ```tsx
 * function DisputeManagement() {
 *   const { disputes, loading, createDispute, resolveDispute } = useDisputes();
 * 
 *   const handleCreateDispute = async (jobId: string) => {
 *     try {
 *       await createDispute({
 *         job_id: jobId,
 *         reason: 'Quality issues - parts do not meet tolerance specifications',
 *         supporting_documents: [
 *           { type: 'inspection_report', url: 'https://...' },
 *           { type: 'photo', url: 'https://...' }
 *         ],
 *         requested_resolution: 'Full refund and return of defective parts'
 *       });
 *       alert('Dispute submitted to admin for review');
 *     } catch (error) {
 *       console.error('Failed to create dispute:', error);
 *     }
 *   };
 * 
 *   return (
 *     <div>
 *       {disputes.map(dispute => (
 *         <DisputeCard key={dispute.id} dispute={dispute} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Admin resolving dispute
 * function AdminDisputeResolution({ dispute }) {
 *   const { resolveDispute } = useDisputes();
 * 
 *   const resolveForClient = async () => {
 *     await resolveDispute(
 *       dispute.id,
 *       'resolved_for_client',
 *       'After review of inspection reports, parts do not meet specs. Full refund issued.'
 *     );
 *   };
 * 
 *   const resolveForVendor = async () => {
 *     await resolveDispute(
 *       dispute.id,
 *       'resolved_for_vendor',
 *       'Parts meet specifications per third-party inspection. Payment released.'
 *     );
 *   };
 * 
 *   return (
 *     <div>
 *       <Button onClick={resolveForClient}>Resolve for Client</Button>
 *       <Button onClick={resolveForVendor}>Resolve for Vendor</Button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useDisputes = () => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches all disputes
   * 
   * Loads disputes ordered by creation date with most recent first.
   * Includes all dispute details, status, and resolution information.
   */
  const fetchDisputes = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('disputes')
        .select(`
          *,
          job:jobs!disputes_job_id_fkey(
            public_id,
            title,
            budget_cents,
            estimated_start_date,
            due_date,
            created_by_user_id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch organization names for each dispute
      if (data && data.length > 0) {
        const orgIds = [...new Set(data.map(d => d.organization_id))];
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name')
          .in('id', orgIds);

        const orgMap = new Map(orgs?.map(o => [o.id, o]) || []);
        
        const disputesWithOrg = data.map(d => ({
          ...d,
          client_organization: orgMap.get(d.organization_id)
        }));

        setDisputes(disputesWithOrg as Dispute[]);
      } else {
        setDisputes([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching disputes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch disputes');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches detailed dispute information including client and vendor details
   */
  const fetchDisputeDetails = async (disputeId: string): Promise<DisputeDetails | null> => {
    try {
      // Fetch dispute with job data
      const { data: dispute, error } = await supabase
        .from('disputes')
        .select(`
          *,
          job:jobs!disputes_job_id_fkey(
            public_id,
            title,
            budget_cents,
            estimated_start_date,
            due_date,
            created_by_user_id,
            organization_id
          )
        `)
        .eq('id', disputeId)
        .single();

      if (error) throw error;
      if (!dispute) return null;

      // Fetch organization name
      const { data: org } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', dispute.organization_id)
        .single();

      const details: DisputeDetails = {
        ...dispute,
        client_organization: org || undefined
      } as DisputeDetails;
      
      // Fetch client profile
      if (dispute.job?.created_by_user_id) {
        const { data: clientProfile } = await supabase
          .from('client_profiles')
          .select('first_name, last_name')
          .eq('user_id', dispute.job.created_by_user_id)
          .single();

        if (clientProfile) {
          details.client = {
            first_name: clientProfile.first_name,
            last_name: clientProfile.last_name,
            organization_name: org?.name
          };
        }
      }

      // Fetch vendor through awards -> bids -> vendor_profiles_enhanced
      const { data: award } = await supabase
        .from('awards')
        .select('winning_bid_id')
        .eq('job_id', dispute.job_id)
        .single();

      if (award?.winning_bid_id) {
        const { data: bid } = await supabase
          .from('bids')
          .select('vendor_user_id')
          .eq('id', award.winning_bid_id)
          .single();

        if (bid?.vendor_user_id) {
          const { data: vendorProfile } = await supabase
            .from('vendor_profiles_enhanced')
            .select('first_name, last_name, business_name')
            .eq('user_id', bid.vendor_user_id)
            .single();

          if (vendorProfile) {
            details.vendor = {
              first_name: vendorProfile.first_name,
              last_name: vendorProfile.last_name,
              business_name: vendorProfile.business_name
            };
          }
        }
      }

      return details;
    } catch (err) {
      console.error('Error fetching dispute details:', err);
      return null;
    }
  };

  /**
   * Updates admin notes for a dispute
   */
  const updateAdminNotes = async (disputeId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('disputes')
        .update({ admin_notes: notes })
        .eq('id', disputeId);

      if (error) throw error;
      
      await fetchDisputes();
    } catch (err) {
      console.error('Error updating admin notes:', err);
      throw err;
    }
  };


  /**
   * Creates a new dispute
   * 
   * Clients or vendors can create disputes when there's a disagreement about
   * job completion, quality, delivery, or payment. Creating a dispute:
   * 1. Inserts dispute record with reason and supporting docs
   * 2. Updates job status to 'disputed'
   * 3. Notifies admin via edge function for review
   * 4. Halts payment processing until resolved
   * 
   * Best Practices:
   * - Provide detailed reason explaining the issue
   * - Attach supporting documents (photos, reports, receipts)
   * - Specify requested resolution clearly
   * - Include timeline of events if relevant
   * 
   * Supporting Documents:
   * Documents should include type and URL. Common types:
   * - inspection_report: Quality inspection results
   * - photo: Visual evidence of issues
   * - receipt: Proof of purchase or delivery
   * - correspondence: Email or message exchanges
   * - test_results: Lab or quality test results
   * 
   * @param disputeData - Dispute details and supporting evidence
   * @returns Created dispute record
   * @throws Error if creation fails or user not authenticated
   * 
   * @example
   * ```typescript
   * const dispute = await createDispute({
   *   job_id: 'job-uuid',
   *   reason: 'Delivered parts have dimensional errors. Measured tolerances exceed ±0.001" specification by up to 0.005" on 15 of 100 parts.',
   *   supporting_documents: [
   *     {
   *       type: 'inspection_report',
   *       url: 'https://storage/inspection-report-20240115.pdf',
   *       description: 'CMM inspection results'
   *     },
   *     {
   *       type: 'photo',
   *       url: 'https://storage/defect-photo-1.jpg',
   *       description: 'Visual of dimensional error'
   *     }
   *   ],
   *   requested_resolution: 'Request full replacement of 15 defective parts at vendor expense, or 15% refund for reduced functionality'
   * });
   * ```
   */
  const createDispute = async (disputeData: {
    job_id: string;
    reason: string;
    dispute_type?: string;
    supporting_documents?: any[];
    requested_resolution?: string;
  }) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      const { data, error } = await supabase
        .from('disputes')
        .insert({
          ...disputeData,
          organization_id: profile.organization_id,
          created_by_user_id: userData.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Update job status to disputed
      await supabase
        .from('jobs')
        .update({ status: 'disputed' })
        .eq('id', disputeData.job_id);

      // Get job details for notification
      const { data: jobData } = await supabase
        .from('jobs')
        .select('id, public_id, title, organization_id')
        .eq('id', disputeData.job_id)
        .single();

      if (jobData) {
        // Notify admins via edge function
        await supabase.functions.invoke('dispute-notify', {
          body: { jobId: disputeData.job_id, disputeId: data.id },
        });
      }

      await fetchDisputes();
      return data;
    } catch (err) {
      console.error('Error creating dispute:', err);
      throw err;
    }
  };

  /**
   * Resolves a dispute (admin only)
   * 
   * Admins review disputes and make final resolution decisions. Resolution
   * determines which party is correct and updates job status accordingly:
   * - resolved_for_client: Client is right, vendor at fault → job 'accepted'
   * - resolved_for_vendor: Vendor is right, claim invalid → job 'rejected'
   * 
   * Resolution Process:
   * 1. Admin reviews evidence from both parties
   * 2. Admin makes determination
   * 3. Dispute record updated with resolution and notes
   * 4. Job status updated based on resolution
   * 5. Payment processed or refunded accordingly
   * 6. Both parties notified of decision
   * 
   * Resolution Notes:
   * Should include:
   * - Summary of issue
   * - Key evidence reviewed
   * - Reasoning for decision
   * - Next steps for both parties
   * 
   * @param disputeId - ID of dispute to resolve
   * @param resolution - Which party the resolution favors
   * @param resolutionNotes - Detailed explanation of decision
   * @returns Updated dispute record
   * @throws Error if resolution fails or user not admin
   * 
   * @example
   * ```typescript
   * // Resolve in client's favor
   * await resolveDispute(
   *   'dispute-uuid',
   *   'resolved_for_client',
   *   `After reviewing the CMM inspection report and photos, the delivered 
   *    parts clearly exceed the specified tolerances. The vendor's claim that 
   *    measurements were taken incorrectly is not supported by evidence.
   *    
   *    Resolution: Full refund of $7,500 issued to client. Vendor to arrange 
   *    pickup of defective parts at their expense within 5 business days.`
   * );
   * 
   * // Resolve in vendor's favor
   * await resolveDispute(
   *   'dispute-uuid',
   *   'resolved_for_vendor',
   *   `Independent third-party inspection confirms all parts meet the 
   *    specified tolerances. Client's measurements appear to have been taken 
   *    with uncalibrated equipment.
   *    
   *    Resolution: Payment of $7,500 released to vendor. Client's dispute 
   *    claim is rejected.`
   * );
   * ```
   */
  const resolveDispute = async (
    disputeId: string,
    resolution: 'resolved_for_client' | 'resolved_for_vendor',
    resolutionNotes?: string
  ) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('disputes')
        .update({
          status: resolution,
          resolution_notes: resolutionNotes,
          resolved_by_user_id: userData.user.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', disputeId)
        .select()
        .single();

      if (error) throw error;

      // Update job status based on resolution
      const finalJobStatus = resolution === 'resolved_for_client' ? 'accepted' : 'rejected';
      await supabase
        .from('jobs')
        .update({ status: finalJobStatus })
        .eq('id', data.job_id);

      await fetchDisputes();
      return data;
    } catch (err) {
      console.error('Error resolving dispute:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  return {
    disputes,
    loading,
    error,
    refetch: fetchDisputes,
    createDispute,
    resolveDispute,
    fetchDisputeDetails,
    updateAdminNotes
  };
};